import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import { toast } from 'react-hot-toast';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import Header from 'shared/components/Header';
import { useAuth } from 'shared/hooks/useAuth';
import useProfilePhotoSrc from 'shared/hooks/useProfilePhotoSrc';
import { softwareApi } from '../../api/systemRegisterApi';
import { formatEnumLabel } from '../../constants/enums';
import { apiErrorMessage, unwrapData } from '../../utils/responseHelpers';
import {
  getReportFormPalette as getFormPalette,
  REPORT_PRINT_KEEP_CSS,
  reportHeaderPrintSx,
} from 'shared/utils/reportFormPalette';
import { downloadFormPdf } from 'shared/utils/downloadFormPdf';

const HEADER_BLUE = '#0D2240';
const FORM_PRINT_ROOT_ID = 'software-risk-form';
const PRINTING_BODY_CLASS = 'sr-printing-software-risk-form';

function dash(value) {
  const v = typeof value === 'string' ? value.trim() : value;
  return v || v === 0 ? String(v) : '';
}

const FieldCell = ({ label, children, sx, colSpan, border, muted }) => (
  <TableCell
    colSpan={colSpan}
    sx={{
      border,
      p: 1.25,
      verticalAlign: 'top',
      color: 'inherit',
      ...sx,
    }}
  >
    <Typography
      component="span"
      sx={{ fontWeight: 700, fontSize: '0.78rem', display: 'block', mb: 0.5, color: 'inherit' }}
    >
      {label}
    </Typography>
    <Box sx={{ minHeight: 22, fontSize: '0.9rem', color: 'inherit', whiteSpace: 'pre-wrap' }}>
      {children || <Box sx={{ color: muted, opacity: 0.7 }}>&nbsp;</Box>}
    </Box>
  </TableCell>
);

function extractRiskFromSoftwareDetail(detail, riskId, riskKey) {
  const list = detail?.softwareRisks ?? [];
  const matched =
    list.find((r) => riskId != null && Number(r.id) === Number(riskId))
    || list.find((r) => riskId != null && Number(r.riskRegisterId) === Number(riskId))
    || list.find((r) => riskKey && String(r.key) === String(riskKey));
  if (!matched) return null;
  return {
    id: matched.id ?? null,
    key: matched.key ?? null,
    riskCategory: matched.riskCategory ?? '',
    resourcesImpacted: matched.resourcesImpacted ?? '',
    riskName: matched.riskName ?? '',
    riskPriority: matched.riskPriority ?? '',
    riskDescription: matched.riskDescription ?? '',
    riskOwner: matched.riskOwner ?? '',
    impact: matched.impact ?? '',
    causes: matched.causes ?? '',
    controlsAndMitigation: matched.controlsAndMitigation ?? '',
    status: matched.status ?? 'OPEN',
  };
}

/**
 * Paper-style Software Risk form view (tenant logo + structured tables).
 */
export default function SoftwareRiskViewPage() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  const querySoftwareId = searchParams.get('softwareId');
  const queryRiskId = searchParams.get('id');

  const [softwareName, setSoftwareName] = useState(location.state?.softwareName || '');
  const [softwareId, setSoftwareId] = useState(location.state?.softwareId ?? null);
  const [risk, setRisk] = useState(() => location.state?.risk || null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const tenantBranding = user?.data?.tenantBranding || user?.data?.branding || null;
  const tenantDisplayName =
    tenantBranding?.tenantName
    || user?.data?.tenantName
    || user?.data?.organisationName
    || 'Organisation';
  const tenantLogoPath =
    tenantBranding?.logoUrl
    || tenantBranding?.tenantPhoto
    || user?.data?.tenantPhoto
    || '';
  const tenantLogoSrc = useProfilePhotoSrc(tenantLogoPath);
  const form = useMemo(() => getFormPalette(isDark, tenantBranding), [isDark, tenantBranding]);

  useEffect(() => {
    if (location.state?.risk) {
      setRisk(location.state.risk);
      setSoftwareName(location.state.softwareName || '');
      setSoftwareId(location.state.softwareId ?? null);
      setLoadError('');
      return;
    }

    if (!querySoftwareId || !queryRiskId) {
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setLoadError('');
      try {
        const response = await softwareApi.get(querySoftwareId);
        const detail = unwrapData(response);
        if (cancelled) return;
        const fromApi = extractRiskFromSoftwareDetail(detail, Number(queryRiskId), null);
        if (!fromApi) {
          setRisk(null);
          setLoadError('Risk not found for this software.');
          return;
        }
        setRisk(fromApi);
        setSoftwareName(detail?.softwareName || '');
        setSoftwareId(detail?.id ?? Number(querySoftwareId));
      } catch (error) {
        if (!cancelled) {
          setRisk(null);
          setLoadError(apiErrorMessage(error, 'Failed to load risk details.'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [location.state, querySoftwareId, queryRiskId]);

  const handleBack = useCallback(() => {
    const returnTo = location.state?.returnTo;
    if (returnTo) {
      navigate(returnTo);
      return;
    }
    if (softwareId) {
      navigate(`/system-register/software/${softwareId}/details`);
      return;
    }
    navigate(-1);
  }, [location.state?.returnTo, navigate, softwareId]);

  const handlePrint = useCallback(() => {
    const previousTitle = document.title;
    document.title = ' ';
    const cleanup = () => {
      document.title = previousTitle;
      document.body.classList.remove(PRINTING_BODY_CLASS);
      window.removeEventListener('afterprint', cleanup);
    };
    document.body.classList.add(PRINTING_BODY_CLASS);
    window.addEventListener('afterprint', cleanup);
    window.print();
    setTimeout(cleanup, 1500);
  }, []);

  const handleDownloadPdf = useCallback(async () => {
    const formEl = document.getElementById(FORM_PRINT_ROOT_ID);
    if (!formEl || !risk || downloadingPdf) return;

    setDownloadingPdf(true);
    try {
      const safeName = (risk.riskName || 'risk').replace(/[^\w.-]+/g, '_').slice(0, 40);
      await downloadFormPdf({
        element: formEl,
        fileName: `software-risk-${safeName}.pdf`,
      });
      toast.success('PDF downloaded.');
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to prepare PDF.'));
    } finally {
      setDownloadingPdf(false);
    }
  }, [downloadingPdf, risk]);

  useEffect(() => {
    const style = document.createElement('style');
    style.setAttribute('data-sr-software-risk-form-print', 'true');
    style.textContent = `
      @page { size: A4; margin: 0; }
      @media print {
        html, body {
          margin: 0 !important;
          padding: 0 !important;
          background: #fff !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        body.${PRINTING_BODY_CLASS} .no-print { display: none !important; }
        body.${PRINTING_BODY_CLASS} * { visibility: hidden !important; }
        body.${PRINTING_BODY_CLASS} #${FORM_PRINT_ROOT_ID},
        body.${PRINTING_BODY_CLASS} #${FORM_PRINT_ROOT_ID} * {
          visibility: visible !important;
        }
        body.${PRINTING_BODY_CLASS} #${FORM_PRINT_ROOT_ID} {
          position: absolute !important;
          left: 0 !important;
          top: 0 !important;
          width: 100% !important;
          max-width: 100% !important;
          margin: 0 !important;
          padding: 12mm !important;
          box-sizing: border-box !important;
          box-shadow: none !important;
          overflow: visible !important;
          border: none !important;
        }
        ${REPORT_PRINT_KEEP_CSS}
      }
    `;
    document.head.appendChild(style);
    return () => {
      style.remove();
      document.body.classList.remove(PRINTING_BODY_CLASS);
    };
  }, []);

  if (loading) {
    return (
      <Box m={{ xs: '8px', sm: '12px' }} display="flex" justifyContent="center" py={8}>
        <CircularProgress size={36} />
      </Box>
    );
  }

  if (!risk) {
    return (
      <Box m={{ xs: '8px', sm: '12px' }}>
        <Header title="Software risk" />
        <Paper variant="outlined" sx={{ p: 3, mt: 2, borderRadius: 2 }}>
          <Typography variant="body1" mb={2}>
            {loadError
              || 'Open a risk from the Risk register using the view action.'}
          </Typography>
          <Button variant="contained" startIcon={<ArrowBackIcon />} onClick={handleBack}>
            Go back
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      m={{ xs: '8px', sm: '12px' }}
      sx={{
        height: 'calc(100vh - 96px)',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        '@media print': {
          height: 'auto',
          m: 0,
          bgcolor: '#fff',
          '& .no-print': { display: 'none !important' },
        },
      }}
    >
      <Box className="no-print">
        <Header title="Software Risk Form" />
      </Box>

      <Box
        className="no-print"
        display="flex"
        justifyContent="flex-end"
        alignItems="center"
        flexWrap="wrap"
        gap={1}
        mb={2}
      >
        <Button type="button" startIcon={<PrintOutlinedIcon />} onClick={handlePrint} variant="outlined">
          Print
        </Button>
        <Button
          type="button"
          startIcon={
            downloadingPdf ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <PictureAsPdfOutlinedIcon />
            )
          }
          onClick={handleDownloadPdf}
          variant="contained"
          color="secondary"
          disabled={downloadingPdf}
        >
          {downloadingPdf ? 'Preparing PDF…' : 'Download PDF'}
        </Button>
      </Box>

      <Paper
        id={FORM_PRINT_ROOT_ID}
        elevation={0}
        sx={{
          overflow: 'auto',
          bgcolor: form.surface,
          color: form.text,
          borderRadius: 0,
          border: form.border,
          maxWidth: 980,
          mx: 'auto',
          width: '100%',
          boxShadow: isDark ? `0 0 0 1px ${alpha('#fff', 0.04)}, 0 8px 28px ${alpha('#000', 0.45)}` : 'none',
          '@media print': {
            border: 'none',
            maxWidth: '100%',
            overflow: 'visible',
            bgcolor: '#fff !important',
            color: '#111 !important',
            boxShadow: 'none',
          },
        }}
      >
        <Box
          sx={{
            bgcolor: form.banner,
            color: form.bannerText,
            px: { xs: 1.5, sm: 2.5 },
            py: 1.75,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
              '@media print': reportHeaderPrintSx,
          }}
        >
          <Box
            sx={{
              width: { xs: 108, sm: 148 },
              height: { xs: 72, sm: 96 },
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: form.logoBg,
              overflow: 'hidden',
              p: 0,
              border: isDark ? `1px solid ${alpha('#fff', 0.2)}` : 'none',
            }}
          >
            {tenantLogoSrc ? (
              <Box
                component="img"
                src={tenantLogoSrc}
                alt={`${tenantDisplayName} logo`}
                sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
              />
            ) : (
              <Typography sx={{ color: HEADER_BLUE, fontWeight: 800, fontSize: '0.7rem', textAlign: 'center' }}>
                LOGO
              </Typography>
            )}
          </Box>

          <Box flex={1} minWidth={140}>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: { xs: '1.15rem', sm: '1.5rem' },
                letterSpacing: 0.2,
                lineHeight: 1.25,
                color: form.bannerText,
              }}
            >
              {tenantDisplayName}
            </Typography>
          </Box>

          <Box textAlign={{ xs: 'left', sm: 'right' }} flexShrink={0}>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: { xs: '0.95rem', sm: '1.15rem' },
                letterSpacing: 0.4,
                lineHeight: 1.25,
                color: form.bannerText,
              }}
            >
              Software Risk Form
            </Typography>
            <Typography sx={{ fontSize: '0.8rem', mt: 0.35, opacity: 0.92, color: form.bannerText }}>
              SR RK1
            </Typography>
          </Box>
        </Box>
        <Table size="small" sx={{ tableLayout: 'fixed', color: 'inherit' }}>
          <TableBody>
            <TableRow>
              <FieldCell label="Software / System Name:" border={form.border} muted={form.muted} sx={{ width: '34%' }}>
                {dash(softwareName) || '—'}
              </FieldCell>
              <FieldCell label="Risk name:" border={form.border} muted={form.muted} sx={{ width: '33%' }}>
                {dash(risk.riskName) || '—'}
              </FieldCell>
              <FieldCell label="Status:" border={form.border} muted={form.muted} sx={{ width: '33%' }}>
                {formatEnumLabel(risk.status) || '—'}
              </FieldCell>
            </TableRow>

            <TableRow>
              <FieldCell label="Risk category:" border={form.border} muted={form.muted}>
                {formatEnumLabel(risk.riskCategory) || '—'}
              </FieldCell>
              <FieldCell label="Risk priority:" border={form.border} muted={form.muted}>
                {formatEnumLabel(risk.riskPriority) || '—'}
              </FieldCell>
              <FieldCell label="Risk owner:" border={form.border} muted={form.muted}>
                {dash(risk.riskOwner) || '—'}
              </FieldCell>
            </TableRow>

            <TableRow>
              <FieldCell label="Resources impacted:" border={form.border} muted={form.muted} colSpan={3}>
                {dash(risk.resourcesImpacted) || '—'}
              </FieldCell>
            </TableRow>

            <TableRow>
              <FieldCell label="Risk description:" border={form.border} muted={form.muted} colSpan={3}>
                {dash(risk.riskDescription) || '—'}
              </FieldCell>
            </TableRow>

            <TableRow>
              <FieldCell label="Impact of the risk:" border={form.border} muted={form.muted} colSpan={3}>
                {dash(risk.impact) || '—'}
              </FieldCell>
            </TableRow>

            <TableRow>
              <FieldCell label="Causes of the risk:" border={form.border} muted={form.muted} colSpan={3}>
                {dash(risk.causes) || '—'}
              </FieldCell>
            </TableRow>

            <TableRow>
              <FieldCell label="Controls and mitigation:" border={form.border} muted={form.muted} colSpan={3}>
                {dash(risk.controlsAndMitigation) || '—'}
              </FieldCell>
            </TableRow>
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}
